import { useState } from "react";

import { Image } from "@/components/atoms/Image";
import { ImageType } from "@/utils/shared-atom-types";
import { getImageUrl } from "@/utils/GetImageUrl";
import { useClassNames } from "@/utils/classNames";

import styles from "./SecondaryNavTile.module.scss";
import { SecondaryNavTileProps } from "./types";

/**
 * SecondaryNavTile component renders a secondary navigation tile to explore brands, sports, and leagues.
 *
 * @param asset - The asset data to be displayed in the tile
 * @param className - Additional CSS class for styling
 *
 * @returns the rendered SecondaryNavTile component
 *
 * @example
 * ```tsx
 * <SecondaryNavTile asset={someAsset}/>
 * ```
 */
const SecondaryNavTile = ({ asset, className }: SecondaryNavTileProps) => {
  const [clicked, setClicked] = useState(false);

  const cn = useClassNames(
    styles,
    styles["secondary-nav-tile"],
    clicked && styles["clicked"],
    className,
  );

  const imageSize = 80;
  const imageUnfocusUrl = getImageUrl(asset, ImageType.ConnectUnfocus, {
    width: imageSize,
    height: imageSize,
  });
  const imageFocusUrl = getImageUrl(asset, ImageType.ConnectFocus, {
    width: imageSize,
    height: imageSize,
  });

  return (
    <div
      className={cn}
      onPointerDown={() => setClicked(true)}
      onPointerUp={() => setClicked(false)}
      onPointerLeave={() => setClicked(false)}
    >
      <Image
        className={useClassNames(styles, styles["img"], styles["focus"])}
        src={imageFocusUrl}
        alt={asset.attributes.title || "Brand logo"}
      />
      <Image
        className={styles["img"]}
        src={imageUnfocusUrl}
        alt={asset.attributes.title || "Brand logo"}
      />
      <span className={styles["text"]}>{asset.attributes.title}</span>
    </div>
  );
};

export { SecondaryNavTile };
